// src/components/kuestion/shared/bankJson.js
//
// The one client-side check of a question-bank JSON file. The Question banks
// upload and the template editor's "Import the reply" both use it, so a reply
// the editor accepts is a file the upload accepts — and the server behind
// both remains the authority.
//
// The schema is the one the server validates and the AI prompt asks for:
//   examCode, module, type ("multichoice" | "draganddrop" | "mixed"),
//   optional totalQuestions, and per question `question`, `type`, `options`,
//   `correctAnswer`.

const REQUIRED_TOP = ['examCode', 'module', 'type', 'questions'];
const REQUIRED_Q = ['question', 'type', 'options', 'correctAnswer'];
const VALID_TYPES = new Set(['multichoice', 'draganddrop', 'mixed']);
const VALID_Q_TYPES = new Set(['multichoice', 'draganddrop']);

/** Every problem found, uncapped. `notJson` marks a body that did not parse. */
export function validateBank(text) {
  let data;
  try { data = JSON.parse(text); } catch (e) { return { ok: false, errors: [`Invalid JSON: ${e.message}`], data: null, notJson: true }; }
  const errors = [];
  if (!data || typeof data !== 'object' || Array.isArray(data)) return { ok: false, errors: ['Root must be a JSON object, not an array or primitive.'], data: null };
  for (const f of REQUIRED_TOP) { if (!data[f] || (typeof data[f] === 'string' && !data[f].trim())) errors.push(`Missing or empty top-level field: "${f}"`); }
  if (errors.length) return { ok: false, errors, data: null };
  const qType = String(data.type || '').toLowerCase().trim();
  if (!VALID_TYPES.has(qType)) errors.push(`Invalid type "${data.type}". Must be: multichoice, draganddrop, or mixed`);
  const questions = data.questions;
  if (!Array.isArray(questions)) return { ok: false, errors: ['"questions" must be an array.'], data: null };
  if (questions.length === 0) return { ok: false, errors: ['"questions" array is empty.'], data: null };
  // Declared count vs actual — catches an LLM response that was truncated
  // mid-array. The server rejects this too; checking here saves a round trip.
  if (data.totalQuestions != null && Number(data.totalQuestions) !== questions.length) {
    errors.push(`File declares ${data.totalQuestions} questions but contains ${questions.length}. It may be truncated.`);
  }
  questions.forEach((q, i) => {
    const n = i + 1;
    if (!q || typeof q !== 'object' || Array.isArray(q)) { errors.push(`Q#${n}: must be an object.`); return; }
    for (const f of REQUIRED_Q) { if (!q[f] || (typeof q[f] === 'string' && !q[f].trim())) errors.push(`Q#${n}: missing/empty "${f}"`); }
    if (!Array.isArray(q.options) || q.options.length < 2) errors.push(`Q#${n}: "options" needs ≥2 items.`);
    const qt = String(q.type || '').toLowerCase().trim();
    if (qt && !VALID_Q_TYPES.has(qt)) errors.push(`Q#${n}: invalid type "${q.type}"`);
  });
  if (errors.length) return { ok: false, errors, data: null };
  return { ok: true, errors: [], data, summary: { examCode: data.examCode, module: data.module, type: data.type, totalQuestions: questions.length } };
}

/** As the Question banks upload shows it: at most ten problems, then a count. */
export function sanitizeJSON(text) {
  const r = validateBank(text);
  if (!r.ok && r.errors.length > 10) return { ...r, errors: [...r.errors.slice(0, 10), `...and ${r.errors.length - 10} more errors.`] };
  return r;
}

const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);

/** The name the prompt asks an assistant to give the file: CODE_module-name.json */
export const bankFileName = (data) => {
  const code = String((data && data.examCode) || 'BANK').replace(/[^A-Za-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30) || 'BANK';
  return `${code}_${slug(data && data.module) || 'module'}.json`;
};

/**
 * An assistant's reply, as pasted. Tolerates a ```json fence and nothing
 * else: an assistant adds one even when told not to, and everything beyond
 * that is the assistant ignoring the prompt.
 * All-or-nothing: either the whole bank, or the reasons and nothing imported.
 */
export function parseBankReply(raw) {
  const text = String(raw || '').trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '').trim();
  if (!text) return { ok: false, errors: ['Paste the reply, or choose the .json file.'] };
  const r = validateBank(text);
  if (r.ok) return { ok: true, text, data: r.data, count: r.data.questions.length, module: String(r.data.module), fileName: bankFileName(r.data) };
  if (r.notJson) return { ok: false, errors: ['This is not valid JSON. Copy the whole reply, from the first { to the last }.'] };
  const e = r.errors;
  return { ok: false, errors: e.length > 6 ? [...e.slice(0, 5), `…and ${e.length - 5} more.`] : e };
}
